<template>
	<div>
		<!-- Search Filter -->
		<BaseSearchFilter :active="isActive" :show="isActive">
			<template #header>
				<CCol>
					<CInput
						v-model="query.keyword"
						:placeholder="$t('placeholder.stock_browser_branch')"
						@keyup.enter="onInputEnter"
					>
						<template #prepend-content>
							<FontAwesomeIcon :icon="['far', 'search']" />
						</template>
					</CInput>
				</CCol>
			</template>
			<template #body>
				<CCol md="3">
					<BaseDropDown
						v-model="filter.branch"
						:options="branchOptions"
						:label-drop-down="$t('label.branch')"
						:placeholder="$t('message.selection')"
						:searchable="true"
						label="name"
						track-by="value"
						@input="handleBranchChange"
					/>
				</CCol>
				<CCol md="3">
					<BaseDropDown
						v-model="filter.location"
						:options="locationOptions"
						:label-drop-down="$t('label.location')"
						:placeholder="$t('message.selection')"
						:searchable="true"
						label="name"
						track-by="value"
						@input="handleLocationChange"
					/>
				</CCol>
			</template>
			<template #footer>
				<CCol md="8">
					{{ $t("message.branches_found", { number: totalCount }) }}
				</CCol>
				<CCol md="4">
					<button @click="onResetClick">
						{{ $t("button.resetFilters") }}
					</button>
				</CCol>
			</template>
		</BaseSearchFilter>

		<!-- Table, Pagination -->
		<BaseTable
			:is-loading="isLoading"
			:pagination="pagination"
			:fields="STOCK_BROWSER_BRANCH_TABLE_FIELDS"
			:items="dataTable"
			:hide-columns="['id']"
			:sort-columns="{
				branch_id:
					query.sort_by === 'branch_id'
						? query.sort_direction || 'asc'
						: 'asc',
				branch_name:
					query.sort_by === 'branch_name'
						? query.sort_direction || 'asc'
						: 'asc'
			}"
			hover
			fixed
			clickable-rows
			name-route="StockBrowserBranchSingle"
			class="table-custom"
			@onSortClick="handleSortClick"
			@onPaginationClick="handlePageClick"
			@onRowClick="handleRowClick"
		>
			<template #no-items-view>
				<div class="empty-table-element">
					<p class="subtitle">
						{{ $t("message.notFoundSkuList") }}
					</p>
				</div>
			</template>
			<template #status="{ item = '' }">
				<CBadge class="badge-sku-status" :color="item.color">
					{{ item.title }}
				</CBadge>
			</template>
		</BaseTable>
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import {
	randomSearchString,
	cleanObject,
	cloneDeep,
	numberFormat,
	isEmpty,
} from '../helpers/utils';
import { getSelectedValue } from '../helpers/dropdown';
import { STOCK_BROWSER_BRANCH_TABLE_FIELDS } from '../enums/stock';
import { ROUTER_NAME } from '../enums/router';

export default {
	name: 'StockBrowserBranchList',

	data() {
		return {
			STOCK_BROWSER_BRANCH_TABLE_FIELDS,
			query: {
				keyword: this.$route.query.keyword ?? null,
				branch: this.$route.query.branch ?? null,
				location: this.$route.query.location ?? null,
				sort_by: this.$route.query.sort_by ?? null,
				sort_direction: this.$route.query.sort_direction ?? null,
				page: this.$route.query.page ?? null,
				r: randomSearchString(),
			},
			filter: {
				branch: getSelectedValue(null, this.branchOptions),
				location: getSelectedValue(null, this.locationOptions),
			},
		};
	},

	computed: {
		...mapState('stockBrowserBranchList', {
			data: 'data',
			isLoading: 'isLoading',
			pagination: 'pagination',
		}),
		...mapGetters('branchList', {
			branchOptions: 'branchOptions',
		}),
		...mapGetters('locationList', {
			locationOptions: 'locationOptions',
		}),
		dataTable() {
			return this.data.map((item) => ({
				id: item.id,
				branch_id: item.branch_id,
				branch_name: item.branch_name,
				total_brand: item.total_brand,
				total_sku: item.total_sku,
				total_stock: item.total_stock,
				online_percentage: item.online_percentage,
				offline_percentage: item.offline_percentage,
				location: item.location,
				last_synced: item.last_synced,
			}));
		},
		totalCount() {
			return numberFormat(this.pagination.total);
		},
		// check any dropdowns are activated
		isActive() {
			if (this.filter.branch?.value !== null) return true;
			if (this.filter.location?.value !== null) return true;

			return false;
		},
	},

	async created() {
		this.fetchData();

		if (this.locationOptions.length === 1) {
			await this.getLocations();
		}

		if (this.branchOptions.length === 1) {
			await this.getBranches();
		}

		this.filter.branch = getSelectedValue(
			!isEmpty(this.$route.query.branch)
				? parseInt(this.$route.query.branch, 10)
				: null,
			this.branchOptions,
		);

		this.filter.location = getSelectedValue(
			!isEmpty(this.$route.query.location)
				? parseInt(this.$route.query.location, 10)
				: null,
			this.locationOptions,
		);
	},

	methods: {
		...mapActions('stockBrowserBranchList', {
			getBranchStocks: 'getBranchStocks',
		}),
		...mapActions('branchList', {
			getBranches: 'getBranches',
		}),
		...mapActions('locationList', {
			getLocations: 'getLocations',
		}),
		fetchData() {
			this.getBranchStocks(this.query);
		},
		onInputEnter(e) {
			this.query.keyword = e.target.value;
			this.query.page = null;
			this.updateUrl();
		},
		onResetClick() {
			this.query.keyword = null;
			this.query.branch = null;
			this.query.location = null;
			this.query.sort_by = null;
			this.query.sort_direction = null;
			this.query.page = null;
			this.query.r = null;
			this.updateUrl();
		},
		handleBranchChange(option) {
			this.query.branch = option?.value ?? null;
			this.query.page = null;
			this.updateUrl();
		},
		handleLocationChange(option) {
			this.query.location = option?.value ?? null;
			this.query.page = null;
			this.updateUrl();
		},
		handleSortClick(key = '', state = false) {
			this.query.page = null;
			this.query.sort_by = key;
			this.query.sort_direction = state;
			this.updateUrl();
		},
		handleRowClick(id) {
			this.$router.push({
				name: ROUTER_NAME.STOCK_BROWSER_BRANCH_SINGLE,
				params: { id },
			});
		},
		handlePageClick(pageNum) {
			this.query.page = pageNum;
			this.updateUrl();
		},
		updateUrl() {
			const query = cleanObject({
				// always put tab, page queries to the `newQuery`
				tab: this.$route.query.tab,
				...cloneDeep(this.query),
			});
			this.$router.push({ query }).catch(() => {});
		},
	},
};
</script>

<style lang="scss" scoped>
::v-deep .table-custom {
	margin-top: rem(32);

	.branch-id {
		// .branch-id-col
		&-col {
			min-width: rem(150);
		}
	}

	.branch-name {
		// .branch-name-col
		&-col {
			min-width: rem(180);
		}
	}

	.total-brand,
	.total-sku,
	.total-stock,
	.online-percentage,
	.offline-stock-col {
		// .total-brand-col, .total-sku-col, .total-stock-col, .online-percentage-col, .offline-stock-col
		&-col {
			min-width: rem(120 + $table-cell-padding);
		}
	}
}
</style>
